import { computed, inject } from '@angular/core';
import { selectActiveBrandId } from '@epicuro-next/state/accounts-state';
import { selectBoughtThisSeasonButtonPropertiesRule } from '@epicuro-next/state/p-basket-state';
import {
  getISODate,
  isNonNullish,
  minusDays,
  uniqueBy,
} from '@epicuro-next/utilities';
import { concatLatestFrom } from '@ngrx/operators';
import { signalStore, withComputed, withMethods } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { select, Store } from '@ngrx/store';
import {
  BoughtThisSeasonProviderArgs,
  BoughtThisSeasonProviderConfigArgs,
  PanelStyle,
  PanelType,
  RecommendedItemProducts,
} from 'models-recommendation';
import { mapProductV2 } from 'product-mapping';
import { filter, map, pipe, tap } from 'rxjs';

import { withSuggestProducts } from './shared/suggest-products-store-feature';

const panelType = PanelType.PBasketBoughtThisSeasonPanel;

// tslint:disable-next-line:variable-name
export const BoughtThisSeasonProductsStore = signalStore(
  withSuggestProducts<RecommendedItemProducts, BoughtThisSeasonProviderArgs>(
    panelType,
    PanelStyle.PBasketBoughtThisSeasonVertical,
  ),
  withComputed((state) => ({
    items: computed(() => {
      const products = state.result
        .dataSet()
        .map(({ item }) => mapProductV2(item));
      return uniqueBy(products, 'parentId');
    }),
  })),
  withMethods((state, store = inject(Store)) => {
    return {
      loadBoughtThisSeasonProducts: rxMethod<void>(
        pipe(
          concatLatestFrom(() => [
            store.pipe(select(selectActiveBrandId), filter(isNonNullish)),
            store.pipe(
              select(selectBoughtThisSeasonButtonPropertiesRule),
              filter(isNonNullish),
            ),
          ]),
          map(
            ([_, brandId, ruleProperties]: [
              void,
              string,
              Partial<BoughtThisSeasonProviderConfigArgs>,
            ]): BoughtThisSeasonProviderArgs => {
              const today = new Date();
              const {
                numberOfDaysFrom,
                numberOfDaysTo,
                ...restRuleProperties
              } = ruleProperties;

              return {
                brandId,
                productsAmount: 1000,
                filterMissingImages: false,
                loadPrices: true,
                type: 'now',
                orderType: 1,
                dateRangeField: 0,
                aggregationType: 0,
                ...restRuleProperties,
                fromDate: getISODate(minusDays(today, numberOfDaysFrom ?? 90)),
                toDate: getISODate(minusDays(today, numberOfDaysTo ?? 1)),
              };
            },
          ),
          tap((payload: BoughtThisSeasonProviderArgs) => {
            state.loadSuggestProducts(payload);
          }),
        ),
      ),
    };
  }),
);
